import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'siq_session_id';
const SESSION_LAST_KEY = 'siq_session_last';
const CONSENT_KEY = 'siq_analytics_consent';
// 30-min sliding window — matches GA-style "session" definition.
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function uuid() {
  // RFC 4122 v4-ish; doesn't need to be cryptographic.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getSessionId() {
  const last = parseInt(localStorage.getItem(SESSION_LAST_KEY) || '0', 10);
  const now = Date.now();
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid || now - last > SESSION_TIMEOUT_MS) {
    sid = uuid();
    localStorage.setItem(SESSION_KEY, sid);
  }
  localStorage.setItem(SESSION_LAST_KEY, String(now));
  return sid;
}

function readUtmParams() {
  const u = new URL(window.location.href);
  return {
    utm_source:   u.searchParams.get('utm_source') || undefined,
    utm_medium:   u.searchParams.get('utm_medium') || undefined,
    utm_campaign: u.searchParams.get('utm_campaign') || undefined,
    utm_content:  u.searchParams.get('utm_content') || undefined,
    utm_term:     u.searchParams.get('utm_term') || undefined,
  };
}

export function hasAnalyticsConsent() {
  return localStorage.getItem(CONSENT_KEY) === 'granted';
}

export function setAnalyticsConsent(granted) {
  localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
}

/**
 * Hook attached at App root.  Tracks:
 *   - Every route change as a page-view (with referrer + UTMs at session start)
 *   - 30-second heartbeat with current scroll depth + cumulative seconds
 *   - Outbound link clicks via a delegated click handler
 * No-ops entirely when consent !== 'granted'.
 */
export function useAnalytics(user) {
  const location = useLocation();
  const lastTickRef = useRef(Date.now());
  const pageEnterRef = useRef(Date.now());
  const maxScrollRef = useRef(0);

  // 1. Page view on every route change
  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    const sid = getSessionId();
    const utms = readUtmParams();
    pageEnterRef.current = Date.now();
    maxScrollRef.current = 0;
    axios.post(`${API}/analytics/page`, {
      session_id: sid,
      path: location.pathname + location.search,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      user_id: user?.id || undefined,
      ...utms,
    }).catch(() => {});
  }, [location.pathname, location.search, user?.id]);

  // 2. Heartbeat with scroll depth + time-on-page
  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    const tick = () => {
      const sid = getSessionId();
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = Math.max(doc.scrollHeight, window.innerHeight, 1);
      const pct = Math.min(100, Math.round((scrolled / total) * 100));
      if (pct > maxScrollRef.current) maxScrollRef.current = pct;
      const now = Date.now();
      const delta = Math.round((now - lastTickRef.current) / 1000);
      lastTickRef.current = now;
      // Skip the first tick (large delta on app load due to React strict mode)
      if (delta > 0 && delta < 120) {
        axios.post(`${API}/analytics/heartbeat`, {
          session_id: sid,
          path: location.pathname + location.search,
          scroll_pct: maxScrollRef.current,
          seconds_on_page: delta,
        }).catch(() => {});
      }
    };
    const id = setInterval(tick, 30 * 1000);
    return () => clearInterval(id);
  }, [location.pathname, location.search]);

  // 3. Outbound link clicks (delegated)
  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    const onClick = (e) => {
      const link = e.target.closest && e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (!href) return;
      // Outbound = different host, mailto, tel, or javascript link with explicit `data-track-cta`.
      let target = '';
      try {
        const u = new URL(href, window.location.href);
        const isExternal = u.host && u.host !== window.location.host;
        const isMailTel = u.protocol === 'mailto:' || u.protocol === 'tel:';
        if (!isExternal && !isMailTel && !link.hasAttribute('data-track-cta')) return;
        target = u.href;
      } catch {
        if (!link.hasAttribute('data-track-cta')) return;
        target = href;
      }
      const sid = getSessionId();
      axios.post(`${API}/analytics/click`, {
        session_id: sid,
        path: location.pathname + location.search,
        target_url: target,
        label: (link.innerText || link.getAttribute('data-track-cta') || '').slice(0, 80),
      }).catch(() => {});
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [location.pathname, location.search]);
}
